import type { CrremDashboardPayload } from "features/crrem-pathway-chart/model/types";

import { pickScenarioBySettings, pickTrendScenarios } from "features/crrem-pathway-chart/lib/parseCrremDashboardApi";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { CrremSettings } from "../model/types";

import {
  buildEnergyCo2ButterflyOption,
  buildEnergyCo2ShareScatterOption,
  buildEnergyCo2TrendOption,
  buildEnergyCo2YearlyScatterOption,
} from "../lib/buildJointOptions";
import { EnergyAnalysisInsightChart } from "./EnergyAnalysisInsightChart";

type EnergyAnalysisJointChartsProps = {
  payload?: CrremDashboardPayload;
  snapshotYear?: number;
  settings?: CrremSettings;
  years: number[];
};

export const EnergyAnalysisJointCharts = ({
  payload,
  settings,
  snapshotYear,
  years,
}: EnergyAnalysisJointChartsProps) => {
  const { t } = useTranslation();
  const scenario = pickScenarioBySettings(pickTrendScenarios(payload, settings?.trendMethod), settings);
  const yearSet = useMemo(() => new Set(years), [years]);
  const input = useMemo(
    () => ({
      co2Rows: (scenario?.co2.rows ?? []).filter((row) => yearSet.has(row.year)),
      co2Unit: scenario?.co2.unit || "kgCO₂",
      energyRows: (scenario?.energy.rows ?? []).filter((row) => yearSet.has(row.year)),
      energyUnit: scenario?.energy.unit || "kWh",
    }),
    [scenario, yearSet]
  );
  const referenceYear = snapshotYear ?? years[years.length - 1];
  const referenceInput = useMemo(
    () => ({
      ...input,
      co2Rows: input.co2Rows.filter((row) => row.year === referenceYear),
      energyRows: input.energyRows.filter((row) => row.year === referenceYear),
    }),
    [input, referenceYear]
  );

  return (
    <div className="grid w-full grid-cols-2 items-stretch gap-4">
      <EnergyAnalysisInsightChart
        height="38rem"
        iconName="Barchart"
        option={buildEnergyCo2ButterflyOption(referenceInput, input)}
        title={t("BuildingProfile.energyAnalysis.joint.butterfly")}
      />
      <EnergyAnalysisInsightChart
        height="38rem"
        iconName="Barchart"
        option={buildEnergyCo2TrendOption(input)}
        title={t("BuildingProfile.energyAnalysis.joint.trend")}
      />
      <EnergyAnalysisInsightChart
        height="36rem"
        option={buildEnergyCo2ShareScatterOption(referenceInput, input)}
        title={t("BuildingProfile.energyAnalysis.joint.shareScatter")}
      />
      <EnergyAnalysisInsightChart
        height="36rem"
        option={buildEnergyCo2YearlyScatterOption(input)}
        title={t("BuildingProfile.energyAnalysis.joint.yearlyScatter")}
      />
    </div>
  );
};
